import { type CountryCode, type ProjectCode, projectNameMap } from '@constants'
import { getCountryCodeFromPathname } from '@utils/getCountryCodeFromPathname'
import { getWordPressProductInfo } from '@utils/getWordPressProductInfo'
import { newURL } from '@utils/url'
import { match } from 'path-to-regexp'
import { type PurchaseEvent, type ViewItemEvent } from '../types/base/ga4-events'
import { type WordpressAddToCartEvent, AddToCartStatus } from '../types/wordpress-ga4-events'
import { type DniCart, type GeneralConfig, type OrderInfo, type OrderItem, type ProductCategoryPageProductList, type Variation } from './wp-types'

declare const window: {
  general_config?: GeneralConfig
  dni_cart?: DniCart
  order_info?: OrderInfo
  cdp_product_list?: ProductCategoryPageProductList[]
}

export const getSiteCurrency = () => window.general_config?.multisite?.site_currency || 'TWD'
export const getItemBrand = (projectCode: ProjectCode): string => projectNameMap[projectCode] ?? projectCode
export const getCountryCode = (pathname: string) => getCountryCodeFromPathname(pathname, 'TW') as CountryCode
export const getWindowOrderInfo = () => window.order_info
export const getWindowDniCart = () => window.dni_cart
export const getPurchaseEventParams = (orderInfo: OrderInfo, projectCode: ProjectCode): PurchaseEvent['eventParams'] => {
  const currency = getSiteCurrency()
  const taxFreeTotal = Number(orderInfo.total) - orderInfo.total_tax
  const totalTax = orderInfo.total_tax
  const transactionId = String(orderInfo.order_id)
  const orderItems = getThankYouPageOrderItems()
  return {
    currency,
    value: taxFreeTotal,
    tax: totalTax,
    transaction_id: transactionId,
    items: orderItems.map(item => ({
      item_id: String(item.variation_id ?? ''),
      item_name: item.name,
      currency,
      price: item.price,
      quantity: item.quantity,
      coupon: item.coupon_code || '',
      item_variant: generateItemVariant({
        productName: item.name,
        attributes: item.variation_attributes,
      }),
      item_brand: getItemBrand(projectCode),
      parent_product_id: item.product_id ? String(item.product_id) : undefined,
      item_flavor: extractItemFlavor({ attributes: item.variation_attributes }),
    })),
  }
}
export const getViewItemEventParams = (variations: Variation[], projectCode: ProjectCode): ViewItemEvent['eventParams'] => {
  const currency = getSiteCurrency()
  const productName = getProductPageProductName()
  const quantity = getProductPageSelectedQuantity()
  const parentProductId = getWordPressProductInfo()?.productId
  const value = variations[0]?.display_price
  return {
    currency,
    value,
    items: variations.map(variation => ({
      currency,
      item_id: String(variation.variation_id || ''),
      item_name: productName,
      price: variation.display_price,
      quantity,
      item_variant: generateItemVariant({
        productName,
        attributes: variation.attributes,
      }),
      item_brand: getItemBrand(projectCode),
      parent_product_id: parentProductId ? String(parentProductId) : undefined,
      item_flavor: extractItemFlavor({ attributes: variation.attributes }),
      item_sku: variation.sku,
    })),
  }
}
export const getAddToCartEventParams = (selectedVariation: Variation, projectCode: ProjectCode): WordpressAddToCartEvent['eventParams'] => {
  const currency = getSiteCurrency()
  const productName = getProductPageProductName()
  const quantity = getProductPageSelectedQuantity()
  const price = Number(selectedVariation.display_price)
  const parentProductId = getWordPressProductInfo()?.productId
  return {
    currency,
    value: price,
    status_code: AddToCartStatus.SUCCESS.code,
    status_message: AddToCartStatus.SUCCESS.message,
    items: [{
      currency,
      item_id: String(selectedVariation.variation_id ?? ''),
      item_name: productName,
      price,
      quantity,
      item_variant: generateItemVariant({
        productName,
        attributes: selectedVariation.attributes,
      }),
      item_brand: getItemBrand(projectCode),
      parent_product_id: parentProductId ? String(parentProductId) : undefined,
      item_flavor: extractItemFlavor({ attributes: selectedVariation.attributes }),
      item_sku: selectedVariation.sku,
    }],
  }
}

export const matchProductPage = (pathname: string) => match('{/:countryCode}/product/:productKey')(pathname)
export const matchMyAccountPage = (pathname: string) => match('{/:countryCode}/my-account')(pathname)
export const matchCartPage = (pathname: string) => match('{/:countryCode}/cart')(pathname)
export const matchThankYouPage = (pathname: string) => match('{/:countryCode}/checkout/order-received/:orderId')(pathname)
export const matchProductCategoryPage = (pathname: string) => match('{/:countryCode}/product-category/*path')(pathname)

export const getProductPageProductVariations = () => {
  const variationsString = document.querySelector('.variations_form')?.getAttribute('data-product_variations')
  if (!variationsString) return []
  const variations: Variation[] = JSON.parse(variationsString)
  return variations
}
export const getProductPageProductName = () => document.querySelector<HTMLHeadingElement>('h1.product-title')?.innerText.trim() || ''
export const isAddToCartButton = (element: HTMLButtonElement | null) => {
  /**
   * For legacy code without product-variation-form
   */
  const isSingleAddToCartButton = element?.matches('.single_add_to_cart_button')
  /**
   * For new code with product-variation-form
   */
  const isAddToCartButton = element?.matches('button[data-action="addToCart"]')
  return isSingleAddToCartButton || isAddToCartButton
}
export const isCheckoutAndConfirmButton = (element: HTMLButtonElement | null) => {
  /**
   * For legacy code
   */
  const isInitCheckoutButton = element?.matches('button[data-dni-init-checkout="true"]')
  return isInitCheckoutButton
}
/**
 * 兼容兩個版本的 product-variation-form
 */
export const getProductPageSelectedQuantity = () => {
  /**
   * 新版 product-variation-form quantity input name 為 product-quantity
   */
  const inputQuantity = document.querySelector<HTMLInputElement>('input[name="product-quantity"]')
  if (inputQuantity) return Number(inputQuantity.value)
  const legacyInputQuantity = document.querySelector<HTMLInputElement>('input[name=quantity]')
  if (legacyInputQuantity) return Number(legacyInputQuantity.value)
  return 0
}
export const getThankYouPageOrderItems = (): OrderItem[] => {
  const value = document.querySelector<HTMLInputElement>('#order_items')?.value
  return value ? JSON.parse(value) : []
}

export const getProductCategoryPageProductList = () => {
  return window.cdp_product_list || []
}

export const getQueryStringFromReferrer = (referrer: string) => {
  const url = newURL(referrer)
  return url?.search
}

export const parseQueryStringToObject = (queryString: string) => {
  return Object.fromEntries(new URLSearchParams(queryString))
}

/**
 * 生成 item_variant 的選項參數
 */
export interface ItemVariantOptions {
  /** 商品名稱 */
  productName?: string
  /** 商品屬性（如口味、規格等） */
  attributes?: Record<string, string>
  /** 是否為寄倉商品 */
  isSubship?: boolean
  /** 總數量 */
  totalQuantity?: number
  /** 配送頻率（月） */
  frequency?: number
  /** 每次幾包 */
  packages?: number
}

/**
 * 標準化 attributes 格式
 * @param attributes - 商品屬性，可以是 Record 格式或 Array 格式
 * @returns 標準化後的 Record<string, string> 格式
 */
export const normalizeAttributes = (attributes: Record<string, string> | { key: string, value: string }[]): Record<string, string> => {
  if (Array.isArray(attributes)) {
    return attributes.reduce((acc, item) => ({ ...acc, [item.key]: item.value }), {})
  }
  return attributes
}

/**
 * 根據商品資訊和業務規則生成 item_variant 字串
 *
 * 規則：
 * 1. 一般商品: product_name / attributes...
 * 2. 寄倉商品: product_name / attributes... / 寄倉 / 總共X包 / Y月一次 / 每次Z包
 *
 * @param options - 生成選項
 * @returns 格式化的 item_variant 字串
 */
export const generateItemVariant = (options: ItemVariantOptions): string => {
  const {
    productName = '',
    attributes = {},
    isSubship = false,
    totalQuantity,
    frequency,
    packages,
  } = options

  const attributeValues = Object.values(attributes).filter(Boolean)
  const baseVariant = attributeValues.length > 0
    ? [productName, ...attributeValues].join(' / ')
    : productName

  if (isSubship) {
    const subshipParts = [
      baseVariant,
      '寄倉',
    ]

    if (totalQuantity) {
      subshipParts.push(`總共${totalQuantity}包`)
    }

    if (frequency) {
      subshipParts.push(`${frequency}月一次`)
    }

    if (packages) {
      subshipParts.push(`每次${packages}包`)
    }

    return subshipParts.join(' / ')
  }
  return baseVariant
}

/* 同 generateItemVariant 但去掉商品名前綴，只留口味 / 規格 */
export const extractItemFlavor = (options: Omit<ItemVariantOptions, 'productName'>): string => {
  const separator = ' / '
  const fullVariant = generateItemVariant({ ...options, productName: '' })
  return fullVariant.startsWith(separator) ? fullVariant.slice(separator.length) : fullVariant
}
